import { OrgStatus, UserRole } from '@fieldday/fielddayportal-model';
import { Chip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Switch, matchPath, useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import Header from '../../components/Global/Header';
import PrivateRoute from '../../components/Global/PrivateRoute';
import Route from '../../components/Global/SuspenseRoute';
import { NpoProfileSkeleton } from '../../components/NonProfits/NpoProfileHeroContent';
import { UpdateNpoStatusForm } from '../../components/Wizard/UpdateStatusForms';
import { OrgProvider, useNonprofitContext } from '../../hooks/commonOrgContext';
import { useAuth } from '../../hooks/useAuth';
import { FieldTripPlanProvider } from '../../hooks/useFieldTripPlanContext';
import { useLoading } from '../../hooks/useLoading';
import { NPO, NPOParser } from '../../models/Npo';
import { useAPI } from '../../util/useAPI';
const ActivityTemplateView  = React.lazy(() => import('../../components/Activities/ActivityTemplateView'))
const ViewFieldTripPlan     = React.lazy(() => import('../FieldTrips/ViewFieldTripPlan'))
const EditScheduledActivity = React.lazy(() => import('../ScheduledActivities/EditScheduledActivity'))
const EditNpo               = React.lazy(() => import('./EditNpo'))
const NpoManagement         = React.lazy(() => import('./NpoManagement'))
const NpoProfile            = React.lazy(() => import('./NpoProfile'))
const ScheduleActivity      = React.lazy(() => import('./ScheduleActivity'))


export default function NonprofitProfileRoutes() {
  const FieldDayAPI = useAPI();
  const { path } = useRouteMatch();
  const { pathname } = useLocation();
  const params: { npohandle: string } = useParams();
  const { npohandle } = params;
  const { loadStart, loadEnd } = useLoading()
  const history = useHistory()
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole | undefined>(undefined)
  const [npo, setNpo] = useState<NPO | null>(null)

  useEffect(() => {
    loadStart()
    let mounted = true
    const apifunc = user ? FieldDayAPI.getNpoWithAuth : FieldDayAPI.getNpo
    apifunc(npohandle).then(response => {
      if (!mounted) return
      const npo = response.nonProfitOrg
      const parsedNPO = new NPOParser(npo).parseNPO()
      if (user && parsedNPO?.orgUnitId) {
        FieldDayAPI.getOrgUnitByIdWithAuth(npo.orgUnitId).then(resp => {
          parsedNPO.orgUnit = resp.orgUnit
          setNpo(parsedNPO)
          // use the original getNpo response role because it might have upgraded a coord to owner
          setRole(response.role)
        })
      } else {
        setNpo(parsedNPO)
        setRole(response.role)
      }
    }).catch(err => {
      if (!mounted) return
      if (err.response?.status === 404) {
        history.replace('/404')
      }
    }).finally(() => {
      loadEnd()
    })
    return () => {
      mounted = false;
    }
  }, [pathname, npohandle, user?.id])

  const manage = matchPath(pathname, { path: `${path}/manage` })
  if (!npo || !role) return manage ? <NonprofitManagementSkeleton /> : <NpoProfileSkeleton />

  return (
    <OrgProvider npo={npo} role={role}>
      <NPOHeader />
      <Switch>
        <Route path={`${path}/activities/:activityid`}>
          <ActivityTemplateView />
        </Route>
        <PrivateRoute path={`${path}/manage/pageinfo`} completeProfileRequired>
          <EditNpo />
        </PrivateRoute>
        <PrivateRoute path={`${path}/manage/events/schedule`} completeProfileRequired>
          <ScheduleActivity />
        </PrivateRoute>
        <PrivateRoute path={`${path}/events/:scheduledactivityid/edit`} completeProfileRequired>
          <EditScheduledActivity />
        </PrivateRoute>
        <PrivateRoute path={`${path}/manage/fieldtrips/:fieldtripplanid`}>
          <FieldTripPlanProvider>
            <ViewFieldTripPlan />
          </FieldTripPlanProvider>
        </PrivateRoute>
        <Route path={`${path}/manage`}>
          <NpoManagement />
        </Route>
        <Route exact path={path}>
          <NpoProfile />
        </Route>
      </Switch>
    </OrgProvider>
  )
}

const NPOHeader = () => {
  const { npo } = useNonprofitContext()

  const name = npo.orgUnit
    ? <>{npo.name}<Chip style={{ marginLeft: "1em" }} color="primary" label={`managed by ${npo.orgUnit.name}`}/></>
    : npo.name

  return (
    <Header
      primaryHeader={name}
      subheader={<SubheaderWithStatusForm npo={npo} />}
      withDivider
    />
  )
}

const SubheaderWithStatusForm = ({ npo }: { npo:NPO }) => {
  const { isWizard } = useAuth()

  // nonprofit is just for status - set it to incoming npo, unless it has been updated otherwise
  const [nonprofit, setNonprofit] = useState<NPO | undefined>(npo ?? undefined)
  // for triggering the update status form
  const [editNonprofitStatus, setEditNonprofitStatus] = useState(false)

  return <>
    {npo.primaryCause}{npo.secondaryCause && `, ${npo.secondaryCause}`}
    { isWizard() && nonprofit &&
      <>
        <Chip label={nonprofit.status} color={nonprofit.status === OrgStatus.Active ? "primary" : "warning"} size="small" sx={{ ml: 1 }}
          onClick={() => setEditNonprofitStatus(true)}
        />
        <UpdateNpoStatusForm open={editNonprofitStatus} setOpen={setEditNonprofitStatus}
          npo={nonprofit} setNpo={setNonprofit}
        />
      </>
    }
  </>
}

function NonprofitManagementSkeleton() {
  return (
    <main>
    </main>
  )
}
